<template>
  <div class="address-form-cnt">
    <div class="address-info">
      <h5 class="title">
        {{ title }}
      </h5>
      <div class="radio-new" :class="[{ disabled: !!oneTimeAddress || !isEnabled }]">
        <input
          id="default-address-radio"
          v-model="defaultAddress"
          value="default"
          type="radio"
          name="address"
          data-cy="default-address-radio"
          :disabled="!!oneTimeAddress" />
        <label for="default-address-radio">{{ $t('oneTimeAddress.default.label') }}</label>
      </div>
      <div v-if="!oneTimeAddress">
        <div class="or">
          <span>{{ $t('viewOrder.default_or') }}</span>
        </div>
        <BackstageButton
          :variant="ButtonVariant.SECONDARY"
          full-width
          :disabled="!isEnabled"
          v-on:click="toggleEditAddress()"
          data-cy-button="one-time-address-button">
          {{ buttonLabel }}
        </BackstageButton>
      </div>
      <div v-if="oneTimeAddress" class="radio-new" :class="[{ disabled: !isEnabled }]">
        <input
          v-model="defaultAddress"
          id="one-time-address-radio"
          value="oneTimeAddress"
          type="radio"
          name="address"
          data-cy="one-time-address-radio" />
        <label for="one-time-address-radio" v-html="oneTimeAddress"></label>
        <div v-if="isEnabled" class="one-time-address-actions">
          <BackstageIcon
            class="icon"
            name="edit"
            data-cy="one-time-address-edit-icon"
            :disabled="!isEnabled"
            v-on:click="toggleEditAddress()">
          </BackstageIcon>
          <BackstageIcon
            class="icon"
            name="trash"
            data-cy="one-time-address-delete-icon"
            :disabled="!isEnabled"
            v-on:click="removeDeliveryAddress()">
          </BackstageIcon>
        </div>
      </div>
    </div>
    <NotificationMessage
      v-if="showEditAddressForm"
      :title="$t('oneTimeAddress.default.popup_title')"
      :size="'small'"
      v-on:close="toggleEditAddress()">
      <AddEditDeliveryAddress
        :delivery-address="deliveryAddress"
        :is-pickup-address="true"
        v-on:cancel="toggleEditAddress()"
        v-on:delivery-address-updated="updateDeliveryAddress">
      </AddEditDeliveryAddress>
    </NotificationMessage>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { AccountAddress } from '@/models/account-address.model';
import { ButtonVariant } from '@/models/button-variant.enum';
import { useUserStore } from '@/stores/user';
import { DeliveryAddressModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent, type PropType } from 'vue';
import NotificationMessage from '../common/NotificationMessage.vue';
import AddEditDeliveryAddress from '../orders/add-edit-delivery-address.vue';

const defaultAddressValue = 'default';
const oneTimeAddressValue = 'oneTimeAddress';

export default defineComponent({
  name: 'one-time-address',
  emits: {
    'delivery-address-updated': (address: AccountAddress | DeliveryAddressModel) => address,
  },
  components: {
    BackstageButton,
    BackstageIcon,
    NotificationMessage,
    AddEditDeliveryAddress,
  },
  props: {
    isEnabled: {
      type: Boolean,
      default: false,
    },
    accountAddress: {
      type: Object as PropType<AccountAddress | DeliveryAddressModel>,
      required: false,
      default: () => new AccountAddress(),
    },
    title: {
      type: String,
      required: true,
    },
    buttonLabel: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());

    return {
      selectedAccount,
      ButtonVariant,
    };
  },
  data() {
    return {
      deliveryAddress: new AccountAddress(),
      defaultAddress: defaultAddressValue,
      editAddressButtonToggled: false,
      updatedAccountAddress: new AccountAddress(),
    };
  },
  computed: {
    oneTimeAddress(): string | undefined {
      const validAddress = this.isValidAddress(this.deliveryAddress);

      const state = this.deliveryAddress?.state ? this.deliveryAddress.state + ' - ' : '';
      const result = validAddress
        ? `${this.deliveryAddress?.street}, ${this.deliveryAddress?.zipCode}<br/>${state}${this.deliveryAddress?.city} - ${this.deliveryAddress?.country}`
        : undefined;

      return result;
    },
    showEditAddressForm(): boolean {
      return this.editAddressButtonToggled && !!this.deliveryAddress;
    },
  },

  beforeMount() {
    this.updatedAccountAddress = this.accountAddress as AccountAddress;

    if (this.updatedAccountAddress && this.isValidAddress(this.updatedAccountAddress)) {
      this.deliveryAddress = this.updatedAccountAddress;
      this.defaultAddress = oneTimeAddressValue;
    }
  },
  methods: {
    isValidAddress(address: AccountAddress) {
      return !!address.city && !!address.zipCode && !!address.street;
    },
    toggleEditAddress() {
      if (!this.editAddressButtonToggled && !this.deliveryAddress?.street) {
        this.deliveryAddress = new AccountAddress();
        this.deliveryAddress.country = this.selectedAccount?.billingCountry;
      }

      this.editAddressButtonToggled = !this.editAddressButtonToggled;
    },
    updateDeliveryAddress(deliveryAddress?: AccountAddress) {
      if (deliveryAddress && this.isValidAddress(deliveryAddress)) {
        this.deliveryAddress = deliveryAddress;
        this.defaultAddress = oneTimeAddressValue;
        this.$emit('delivery-address-updated', this.deliveryAddress);
      }

      this.toggleEditAddress();
    },
    removeDeliveryAddress() {
      this.deliveryAddress = new AccountAddress();
      this.defaultAddress = defaultAddressValue;

      this.$emit('delivery-address-updated', this.deliveryAddress);
    },
  },
});
</script>

<style lang="scss" scoped>
@mixin flex-cnt {
  display: flex;
  gap: 12px;
}

.address-form-cnt {
  margin-bottom: 12px;

  .address-info {
    .one-time-address-actions {
      display: inherit;
    }

    .title {
      font-size: 12px;
      text-align: left;
      line-height: 150%;
      margin: 0;
      font-weight: 400;
      color: var(--primary-label-input-color);
    }

    .or {
      width: 100%;
      height: 1px;
      background-color: var(--border-inactive-color);
      position: relative;
      margin: 21px 0;

      span {
        display: block;
        padding: 0 10px;
        background: var(--primary-bg-color);
        color: var(--primary-text-remark-color);
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
      }
    }

    .radio-new {
      width: 100%;
      margin: 12px 0;

      .icon,
      i.ico {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }
    }
  }
}
</style>
